import { UserDto } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { getUserRoleTranslationKey } from "@/common/constants/translations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Calendar, Camera, Loader2, Mail, Phone } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ProfileHeaderProps {
  user: UserDto;
}

export const ProfileHeader = ({ user }: ProfileHeaderProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { uploadAvatarMutation } = useUsersQuery();

  const { mutateAsync: uploadAvatar } = useMutation({
    ...uploadAvatarMutation,
  });

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: t("profile.image.toast.error.title"),
        description: t("profile.image.toast.error.sizeLimit"),
      });
      return;
    }

    if (!file.type.match(/^image\/(jpg|jpeg|png|gif|webp)$/i)) {
      toast({
        variant: "destructive",
        title: t("profile.image.toast.error.title"),
        description: t("profile.image.toast.error.invalidType"),
      });
      return;
    }

    setIsLoading(true);

    try {
      await uploadAvatar({
        userUUID: user.userUUID,
        file,
      });

      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["users", user.userUUID] });

      toast({
        title: t("profile.image.toast.success.title"),
        description: t("profile.image.toast.success.description"),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("profile.image.toast.error.title"),
        description: t("profile.image.toast.error.description"),
      });
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="h-32 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10" />
      <CardContent className="relative p-6">
        <div className="flex flex-col items-center sm:flex-row sm:items-start sm:space-x-6">
          <div className="relative -mt-16">
            <Avatar
              className="h-32 w-32 border-4 border-background cursor-pointer"
              onClick={handleImageClick}
            >
              {user.avatarData?.fullSize?.url ? (
                <AvatarImage
                  src={user.avatarData.fullSize.url}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="object-cover"
                />
              ) : user.imageGoogleUrl ? (
                <AvatarImage
                  src={user.imageGoogleUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="object-cover"
                />
              ) : null}
              <AvatarFallback className="text-2xl">
                {isLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : user.firstName && user.lastName ? (
                  `${user.firstName[0]}${user.lastName[0]}`
                ) : (
                  "UK"
                )}
              </AvatarFallback>
            </Avatar>
            <Button
              size="icon"
              variant="secondary"
              className="absolute bottom-0 right-0 h-8 w-8 rounded-full shadow-lg"
              onClick={handleImageClick}
              disabled={isLoading}
              aria-label={t("profile.image.upload.aria")}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpg,image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              aria-label={t("profile.image.upload.aria")}
            />
          </div>

          <div className="mt-6 flex flex-col space-y-4 text-center sm:mt-0 sm:text-left">
            <div>
              <h2 className="text-2xl font-bold">
                {user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.email}
              </h2>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <Badge variant="secondary" className="text-xs">
                  {t(getUserRoleTranslationKey(user.role as any))}
                </Badge>
                {user.isActive && (
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800 text-xs"
                  >
                    {t("users.status.options.active")}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>{user.email}</span>
              </div>
              {user.phoneNumber && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{user.phoneNumber}</span>
                </div>
              )}
              {user.birthDate && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(user.birthDate), "d MMMM yyyy")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
